import { CaretDown, User } from "@phosphor-icons/react"
import { Checkbox, CodenameTag, Tag } from "@vesatogo/grass-core"
import { inr, percent } from "@vesatogo/utils"
import classNames from "classnames"
import { Fragment } from "react"
import { Base_User, Setu_Trade_Shared_Entity, Trade } from "~/generated/graphql"

export type TraderSummaryProps = {
  isExpanded?: boolean
  onExpandToggle?: () => void
  isSelected?: boolean
  onSelect?: () => void
  trader?: Partial<Base_User>
  trades?: Partial<Trade>[]
  rate_unit?: Partial<Setu_Trade_Shared_Entity>
  quantity_unit?: Partial<Setu_Trade_Shared_Entity>
  standard_deduction?: any
  expense_amount?: any
  total_quantity?: any
  total_amount?: any
  tax_amount?: any
}

const groupItems = (items: any[]): any[] =>
  Object.values(
    items.reduce((acc: any, item: any) => {
      const key =
        item?.commodity_variety?.id ??
        item?.commodity?.id ??
        (item?.commodity_variety?.name || item?.commodity?.name || "na")
      if (!acc[key]) {
        acc[key] = {
          commodity: item?.commodity,
          variety: item?.commodity_variety,
          grades: [],
        }
      }
      acc[key].grades.push(item)
      return acc
    }, {})
  )

const TraderSummary = ({
  isExpanded,
  isSelected,
  onSelect,
  onExpandToggle,
  trader: user,
  trades,
  rate_unit,
  quantity_unit,
  standard_deduction,
  expense_amount,
  total_quantity,
  total_amount,
  tax_amount,
}: TraderSummaryProps) => {
  const taxAmount = Number(tax_amount || 0)

  const itemAmount = (item: any) =>
    Number(
      item?.amount ?? (Number(item?.quantity) || 0) * (Number(item?.rate) || 0)
    ) || 0

  const itemGst = (item: any) =>
    (item?.taxes || []).reduce(
      (sum: number, t: any) => sum + (Number(t?.amount) || 0),
      0
    )

  const tradeGroups = (trades || []).map(trade => {
    const items = (trade?.trade_items || []).filter((item: any) => item?.id)
    return {
      trade,
      groups: groupItems(items).map(g => ({
        ...g,
        material: g.grades.reduce(
          (sum: number, it: any) => sum + itemAmount(it),
          0
        ),
        itemGst: g.grades.reduce(
          (sum: number, it: any) => sum + itemGst(it),
          0
        ),
      })),
    }
  })

  const totalItemGst = tradeGroups.reduce(
    (sum, tg) => sum + tg.groups.reduce((gs, g) => gs + g.itemGst, 0),
    0
  )
  const totalMaterial = tradeGroups.reduce(
    (sum, tg) => sum + tg.groups.reduce((gs, g) => gs + g.material, 0),
    0
  )

  const gstForGroup = (g: { material: number; itemGst: number }) => {
    if (totalItemGst > 0) return g.itemGst
    if (taxAmount > 0 && totalMaterial > 0)
      return (g.material / totalMaterial) * taxAmount
    return 0
  }

  return (
    <div className="border rounded-lg mb-3 last-of-type:mb-0">
      <div
        className={classNames(
          "bg-green-100 p-3 flex justify-between items-center",
          isExpanded ? "rounded-t-lg" : "rounded-lg"
        )}
      >
        <div>
          <div>
            {onSelect ? (
              <Checkbox checked={isSelected} onClick={onSelect} />
            ) : null}
            <p
              className={classNames(
                "flex items-center mb-[2px]",
                onSelect && "ml-7"
              )}
            >
              <span>
                {user?.full_name || "na"}{" "}
                <span className="text-gray-600">(Trader Name)</span>
              </span>
            </p>
          </div>
          <CodenameTag
            referenceId={user?.external_reference_id as string}
            leftIcon={<User weight="fill" />}
          />
        </div>
        {onExpandToggle ? (
          <CaretDown
            style={{
              rotate: isExpanded ? "0deg" : "180deg",
            }}
            onClick={onExpandToggle}
            className="cursor-pointer hover:opacity-70"
            size={24}
          />
        ) : null}
      </div>
      {(isExpanded || !onExpandToggle) && (
        <div className="p-3">
          <table className="w-full border rounded-md overflow-hidden">
            <thead>
              <tr className="bg-gray-200 text-left text-xs text-gray-600">
                <th className="px-[10px] py-[7px]">Supplier</th>
                <th className="px-[10px] py-[7px]">Commodity</th>
                <th className="px-[10px] py-[7px]">Grade</th>
                <th className="px-[10px] py-[7px]">Rate</th>
                <th className="px-[10px] py-[7px]">Quantity</th>
                <th className="px-[10px] py-[7px]">Amount</th>
              </tr>
            </thead>
            <tbody>
              {tradeGroups.map(({ trade, groups }) => {
                const farmer = trade?.seller?.user?.[0]
                const supplierSpan =
                  groups.reduce(
                    (sum, g) => sum + Math.max(g.grades.length, 1) + 1,
                    0
                  ) + 1

                const supplierTotal =
                  (Number(trade?.total_amount) ||
                    groups.reduce(
                      (sum, g) =>
                        sum +
                        g.grades.reduce((gs, it) => gs + itemAmount(it), 0),
                      0
                    )) + groups.reduce((sum, g) => sum + gstForGroup(g), 0)

                return (
                  <Fragment key={trade.id}>
                    {groups.map((g, gIdx) => {
                      const firstRowOfSupplier = gIdx === 0
                      const rows = g.grades.map((item, rowIdx) => {
                        const isFirstGradeRow = rowIdx === 0
                        return (
                          <tr
                            key={`${trade.id}-${gIdx}-${rowIdx}`}
                            className="border-b border-gray-100 !text-sm"
                          >
                            {firstRowOfSupplier && rowIdx === 0 && (
                              <td
                                rowSpan={supplierSpan}
                                className="px-[10px] py-[7px] align-top font-500"
                              >
                                <div className="flex flex-wrap gap-2 items-center">
                                  {farmer?.full_name || "na"}
                                  <CodenameTag
                                    leftIcon={<User weight="fill" />}
                                    referenceId={
                                      farmer?.external_reference_id || "na"
                                    }
                                  />
                                </div>
                              </td>
                            )}
                            {isFirstGradeRow && (
                              <td
                                rowSpan={Math.max(g.grades.length, 1)}
                                className="px-[10px] py-[7px] align-top text-gray-800"
                              >
                                {g.commodity?.name ||
                                  g.variety?.name ||
                                  "Commodity"}
                                <div className="text-gray-600">
                                  {g.variety?.name ||
                                    g.commodity?.name ||
                                    "Commodity variety"}
                                </div>
                              </td>
                            )}
                            <td className="px-[10px] py-[7px]">
                              <Tag
                                intent="info"
                                className="!rounded-xl"
                                variant="minimal"
                              >
                                {item.variety_grade?.name || "—"}
                              </Tag>
                            </td>
                            <td className="px-[10px] py-[7px] text-brown font-600">
                              {`${inr(item.rate)} / ${rate_unit?.name || "na"}`}
                            </td>
                            <td className="px-[10px] py-[7px] text-green font-600">
                              {item.quantity} {quantity_unit?.name || "na"}
                            </td>
                            <td className="px-[10px] py-[7px] text-green font-600">
                              {inr(itemAmount(item))}
                            </td>
                          </tr>
                        )
                      })
                      const subTotal = g.material + gstForGroup(g)
                      const totalRow = (
                        <tr
                          key={`${trade.id}-${gIdx}-total`}
                          className="bg-gray-50 border-b border-gray-100 !text-sm"
                        >
                          <td
                            className="px-[10px] py-[7px] font-600"
                            colSpan={3}
                          >
                            Sub Total{" "}
                            <span className="text-gray-600 font-400">
                              {" "}
                              ((i.e. Trade total + Commodity GST))
                            </span>
                          </td>
                          <td />
                          <td className="px-[10px] py-[7px]  font-700">
                            {inr(subTotal)}
                          </td>
                        </tr>
                      )
                      return [...rows, totalRow]
                    })}
                    <tr
                      key={`${trade.id}-supplier-total`}
                      className="bg-yellow-50 border-b border-gray-100 text-base font-700"
                    >
                      <td className="px-[10px] py-[7px] font-600" colSpan={3}>
                        Supplier Total
                      </td>
                      <td className="px-[10px] py-[7px] text-right" colSpan={2}>
                        {inr(supplierTotal)}
                      </td>
                    </tr>
                  </Fragment>
                )
              })}
              <tr className="!text-sm font-600">
                <td className="px-[10px] py-[7px] text-red-500">
                  Average Standard deduction
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td className="px-[10px] py-[7px] text-red-500">
                  {percent(standard_deduction)}
                </td>
              </tr>
              <tr className="!text-sm font-600">
                <td className="px-[10px] py-[7px] text-red-500">Expense</td>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td className="px-[10px] py-[7px] text-red-500">
                  {inr(expense_amount)}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td className="px-[10px] py-[7px] font-600 text-gray-700">
                  Invoice Total
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td className="px-[10px] py-[7px] font-600 text-gray-700">
                  {/* {total_quantity?.toLocaleString("en-IN")}{" "}
                  {quantity_unit?.name} */}
                </td>
                <td className="px-[10px] py-[7px] font-600 text-gray-700">
                  {inr(Number(total_amount || 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}

export default TraderSummary
